// Persistent storage for achievements + per-game stats.
//
// Schema (localStorage):
//   retroplay-achievements → { version, unlocked: { "nes:abcd1234": { "ach-id": ts, ... }, ... } }
//   retroplay-stats        → { version, games:    { "nes:abcd1234": {
//                               playMs, sessions, lastPlayed,
//                               firstPlayed, longestSessionMs,
//                               highScore, highScoreAt
//                            }, ... } }
//
// Keys are `${systemId}:${romHash}` so the same cartridge on different
// systems can't collide. Version field is bumped when we change shape
// so `migrate()` has something to branch on.
//
// Each tab updates its cache synchronously. Every persistence writer uses one
// IndexedDB readwrite scope to serialize its whole localStorage critical section
// across tabs, including migrations and deletions. Saves are best effort until
// whenPersisted settles; destructive acknowledgements await transaction completion.
// An abrupt close can lose queued local cache writes. Server data remains authoritative.

// The stats shape is designed to map row-for-row onto a future
// `play_stats(user_id, system_id, rom_hash, play_ms, sessions,
//             first_played, last_played, longest_session_ms,
//             high_score, high_score_at)` table — when the server
// back-end lands, sync is a dumb upload of `games[]`.

import { isKnownScoreContext, normalizeScoreEpoch, normalizeScoreVersionId } from './score-context.js';
import { primaryHash } from '../../shared/rom-hash.js';
import { storageTransaction } from './storage-transaction.js';

const ACHIEVEMENTS_KEY = 'retroplay-achievements';
const STATS_KEY = 'retroplay-stats';
const ACHIEVEMENTS_VERSION = 1;
const STATS_VERSION = 2;

/**
 * One hash identity per game: a GB dual-hash session's hash array is keyed by its primary hash, as
 * the server, saves and the room key it (shared/rom-hash.js), so capture, reads, sends and discards
 * all reach the same record whichever form a caller holds.
 */
function gameKey(systemId, romHash) {
  return `${systemId}:${primaryHash(romHash)}`;
}

/** A stored key's canonical form: a key written from a hash array (`system:h1,h2`) keeps its primary hash. */
function canonicalKey(key) {
  const colon = key.indexOf(':');
  const comma = colon < 0 ? -1 : key.indexOf(',', colon + 1);
  return comma < 0 ? key : key.slice(0, comma);
}

const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const isRecord = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

/** Move every array-keyed entry of `maps` onto its canonical key, merged by `merge(into, from)`. */
function foldKeys(maps, merge) {
  if (!isRecord(maps)) return;
  for (const key of Object.keys(maps)) {
    const target = canonicalKey(key);
    if (target === key) continue;
    const from = maps[key];
    delete maps[key];
    if (!isRecord(from)) continue;
    if (isRecord(maps[target])) merge(maps[target], from);
    else maps[target] = from;
  }
}

/** Unlock maps: the earliest unlock of each id; recorded cores: the canonical record's first. */
function mergeUnlocks(into, from) {
  for (const [id, ts] of Object.entries(from)) {
    if (!own(into, id) || (Number.isFinite(ts) && !(into[id] <= ts))) into[id] = ts;
  }
}
function mergeCores(into, from) {
  for (const [id, core] of Object.entries(from)) if (!own(into, id)) into[id] = core;
}

/** Stats records: play time and sessions add up, the dates span both, each best keeps the higher one. */
function mergeStats(into, from) {
  into.playMs = (into.playMs || 0) + (from.playMs || 0);
  into.sessions = (into.sessions || 0) + (from.sessions || 0);
  into.lastPlayed = Math.max(into.lastPlayed || 0, from.lastPlayed || 0);
  const firsts = [into.firstPlayed, from.firstPlayed].filter((t) => Number.isFinite(t) && t > 0);
  into.firstPlayed = firsts.length ? Math.min(...firsts) : 0;
  into.longestSessionMs = Math.max(into.longestSessionMs || 0, from.longestSessionMs || 0);
  if (Number.isFinite(from.highScore) && !(into.highScore >= from.highScore)) {
    into.highScore = from.highScore;
    into.highScoreAt = from.highScoreAt || 0;
    if (from.highScoreCore) into.highScoreCore = from.highScoreCore; else delete into.highScoreCore;
  }
  if (isRecord(from.authoredBests)) {
    const bests = isRecord(into.authoredBests) ? into.authoredBests : (into.authoredBests = {});
    for (const [key, best] of Object.entries(from.authoredBests)) {
      if (!own(bests, key) || !(bests[key]?.highScore >= best?.highScore)) bests[key] = best;
    }
  }
  if (into.activeScoreKey === undefined && from.activeScoreKey !== undefined) into.activeScoreKey = from.activeScoreKey;
}

const STATIC_SCORE_KEY = 'static';

/** One cached best per authored version and epoch (spec 7.9), or null. */
export function authoredBestKey(scoreVersionId, scoreEpoch) {
  const version = normalizeScoreVersionId(scoreVersionId);
  const epoch = normalizeScoreEpoch(scoreEpoch);
  return version == null || epoch == null ? null : `v${version}:e${epoch}`;
}

/** A context a score can be captured under (the senders' rule too). */
const isCaptureContext = isKnownScoreContext;

/** The authored best cached under `key`, only with that key's own version and epoch; else null (unusable: never read as static). */
function authoredBestAt(g, key) {
  const bests = g.authoredBests;
  const best = bests && Object.prototype.hasOwnProperty.call(bests, key) ? bests[key] : null;
  if (best === null || typeof best !== 'object' || authoredBestKey(best.scoreVersionId, best.scoreEpoch) !== key
      || !Number.isFinite(best.highScore) || best.highScore <= 0) return null;
  return {
    highScore: best.highScore, highScoreAt: Number.isFinite(best.highScoreAt) ? best.highScoreAt : 0,
    coreName: typeof best.coreName === 'string' && best.coreName !== '' ? best.coreName : null,
    scoreVersionId: normalizeScoreVersionId(best.scoreVersionId), scoreEpoch: best.scoreEpoch,
  };
}

/**
 * What a score captured under `bestKey` changes in stats record `g`: `key` when it moves the active key,
 * `best` when it beats the best at that key (the higher wins; an unusable authored record is replaced).
 */
function scoreChange(g, bestKey, score) {
  if (bestKey === STATIC_SCORE_KEY) {
    const current = g.highScore == null ? -Infinity : g.highScore;
    return { key: g.activeScoreKey !== undefined && g.activeScoreKey !== STATIC_SCORE_KEY, best: !(score <= current) };
  }
  const best = authoredBestAt(g, bestKey);
  return { key: g.activeScoreKey !== bestKey, best: !best || score > best.highScore };
}

/** Record that score in `g` (spec 7.9): `{ result, changed }`, the result true for a new best. */
function recordScore(g, bestKey, score, now, ctx) {
  const change = scoreChange(g, bestKey, score);
  if (change.key) g.activeScoreKey = bestKey;
  if (change.best && bestKey === STATIC_SCORE_KEY) {
    g.highScore = score;
    g.highScoreAt = now;
    if (ctx?.coreName) g.highScoreCore = ctx.coreName;
  } else if (change.best) {
    // Per version and epoch: a clear or a new version starts fresh.
    (g.authoredBests || (g.authoredBests = {}))[bestKey] = {
      highScore: score, highScoreAt: now, coreName: ctx.coreName || null,
      scoreVersionId: normalizeScoreVersionId(ctx.scoreVersionId), scoreEpoch: ctx.scoreEpoch,
    };
  }
  return { result: change.best, changed: change.key || change.best };
}

/** Stores `value` or throws (`code: 'store_write_failed'`, the storage error as `cause`). */
function writeJSONOrThrow(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (cause) {
    throw Object.assign(new Error(`${key} was not stored`, { cause }), { code: 'store_write_failed' });
  }
}

/**
 * The stored value of `key` for a destructive reconciliation, read strictly: `{ ok: true, value }`
 * (null when absent), or `{ ok: false, code, cause }` when storage refuses the read
 * (`store_read_failed`) or the stored text does not parse (`store_unreadable`), never a fallback.
 */
function readJSONStrict(key) {
  let raw;
  try {
    raw = localStorage.getItem(key);
  } catch (cause) {
    return { ok: false, code: 'store_read_failed', cause };
  }
  if (!raw) return { ok: true, value: null };
  try {
    return { ok: true, value: JSON.parse(raw) };
  } catch (cause) {
    return { ok: false, code: 'store_unreadable', cause };
  }
}

/** A strict read's refusal as the structured failure every removal answers (`{ ok: false, code, cause }`). */
function readRefusal(read) {
  return Object.freeze({ ok: false, code: read.code, cause: read.cause });
}

/**
 * Remove an entry from this tab's cache (`cached`) and from the latest persisted document of
 * `storageKey`, read strictly and written back with only that entry gone, so another tab's newer
 * records stay (the cache, which may predate them, is never written). `present(doc)` says whether
 * a document holds the entry, `entries(doc)` lists its `[map, id]` places. Answers true once its
 * absence is stored (when only the cache held it, storage already lacks it), false when neither
 * holds it, the structured failure `{ ok: false, code, cause }` (store_read_failed, store_unreadable)
 * when the persisted document cannot be read or parsed (nothing removed: never acknowledged from the
 * cache alone), and throws `store_write_failed` with every removal restored.
 */
function removeEverywhere(storageKey, migrate, cached, present, entries) {
  const read = readJSONStrict(storageKey);
  if (!read.ok) return readRefusal(read);
  const stored = migrate(read.value);
  const docs = [cached, stored];
  if (!docs.some(present)) return false;
  const taken = [];
  for (const doc of docs) {
    for (const [map, id] of entries(doc)) {
      if (isRecord(map) && own(map, id)) { taken.push([map, id, map[id], doc === stored]); delete map[id]; }
    }
  }
  try {
    if (taken.some(([, , , inStored]) => inStored)) writeJSONOrThrow(storageKey, stored);
  } catch (error) {
    for (const [map, id, value] of taken) map[id] = value;   // kept for the retry
    throw error;
  }
  return true;
}

/** Read and migrate a tab's cache without writing. The constructor separately
 * queues a fresh migration inside the shared serialization scope. */
function loadDocument(key, migrate) {
  const read = readJSONStrict(key);
  if (!read.ok) return migrate(null);
  return migrate(read.value); // Only the serialized constructor migration may write.
}

function emptyAchievements() {
  return { version: ACHIEVEMENTS_VERSION, unlocked: {} };
}

function emptyStats() {
  return { version: STATS_VERSION, games: {} };
}

/** Zero-valued stats record. Every field the UI reads has a default. */
function emptyStatsRecord() {
  return {
    playMs: 0,
    sessions: 0,
    lastPlayed: 0,
    firstPlayed: 0,
    longestSessionMs: 0,
    highScore: null,
    highScoreAt: 0,
  };
}

/**
 * Migrate the achievements blob. We only have one version right now —
 * unversioned blobs are treated as corrupt and cleared.
 */
function migrateAchievements(obj) {
  if (!obj || typeof obj !== 'object') return emptyAchievements();
  if (obj.version !== ACHIEVEMENTS_VERSION) return emptyAchievements();
  // Records an older client kept under a dual-hash array key join their primary hash's (gameKey).
  foldKeys(obj.unlocked, mergeUnlocks);
  foldKeys(obj.cores, mergeCores);
  return obj;
}

/**
 * Migrate the stats blob. v1 → v2 is additive: fill the new fields
 * from what's already there (firstPlayed defaults to lastPlayed so
 * pre-existing games get a sensible "first played" date instead of 0).
 */
function migrateStats(obj) {
  const migrated = migrateStatsVersion(obj);
  foldKeys(migrated.games, mergeStats);   // dual-hash array keys join their primary hash's (gameKey)
  return migrated;
}

function migrateStatsVersion(obj) {
  if (!obj || typeof obj !== 'object') return emptyStats();
  if (obj.version === STATS_VERSION) return obj;
  if (obj.version === 1 && obj.games && typeof obj.games === 'object') {
    const upgraded = { version: STATS_VERSION, games: {} };
    for (const [key, g] of Object.entries(obj.games)) {
      upgraded.games[key] = {
        playMs: g.playMs || 0,
        sessions: g.sessions || 0,
        lastPlayed: g.lastPlayed || 0,
        firstPlayed: g.lastPlayed || 0,
        longestSessionMs: 0,
        highScore: null,
        highScoreAt: 0,
      };
    }
    return upgraded;
  }
  return emptyStats();
}

export class AchievementStore {
  constructor() {
    this._serverMode = false;
    this._persistence = new Set();
    this._persistenceFailures = [];
    this._pendingChanges = [];
    this._cacheGeneration = 0;
    this._a = loadDocument(ACHIEVEMENTS_KEY, migrateAchievements);
    this._s = loadDocument(STATS_KEY, migrateStats);
    // Per-session ephemeral map: gameKey → session-start timestamp.
    // Drives longestSessionMs on endSession(). Not persisted — a crash
    // or reload forgets the in-progress session length, which is fine.
    this._sessionStartAt = {};
    this._contextProvider = null;
    this._coordinate(() => {
      for (const [key, migrate] of [[ACHIEVEMENTS_KEY, migrateAchievements], [STATS_KEY, migrateStats]]) {
        const read = readJSONStrict(key);
        if (!read.ok) continue;
        const before = JSON.stringify(read.value);
        const doc = migrate(read.value);
        if (JSON.stringify(doc) !== before) writeJSONOrThrow(key, doc);
      }
    }).catch(() => {});
  }

  /** Join work queued so far without rejecting gameplay teardown. */
  whenPersisted() {
    return Promise.allSettled([...this._persistence]).then(() => {
      const failures = this._persistenceFailures.splice(0);
      return { ok: failures.length === 0, failures };
    });
  }

  _coordinate(run, guard = () => null) {
    const task = storageTransaction(() => {
      const code = guard();
      return code ? { ok: false, code } : run();
    });
    this._persistence.add(task);
    task.then(() => this._persistence.delete(task), cause => {
      this._persistence.delete(task);
      this._persistenceFailures.push(cause);
      if (this._persistenceFailures.length > 8) this._persistenceFailures.shift();
    });
    return task;
  }

  /** A serialized result plus mutations invoked later, never an older cache snapshot. */
  _refresh(field, doc) {
    for (const pending of this._pendingChanges) if (pending.field === field) pending.apply(doc);
    this[field] = doc;
  }

  enableServerMode() {
    this._serverMode = true;
  }

  /** The live context, `(systemId, romHash) => ctx | null`. */
  setContextProvider(fn) {
    this._contextProvider = typeof fn === 'function' ? fn : null;
  }

  /** `{ ok: true, ctx }`, or `{ ok: false }` when the provider gave nothing usable. The provider is asked for the game's one hash identity (gameKey). */
  _captureContext(systemId, romHash) {
    if (!this._contextProvider) return { ok: true, ctx: null };
    let ctx;
    try {
      ctx = this._contextProvider(systemId, primaryHash(romHash));
    } catch {
      return { ok: false };
    }
    return isCaptureContext(ctx) ? { ok: true, ctx } : { ok: false };
  }

  /** Update memory immediately, then queue this delta against the latest stored
   * document under cross-tab exclusion. The synchronous result describes this
   * tab's capture, not durability or the final merged best. Refreshes replay
   * later queued local deltas so an older completion cannot erase them. Failed
   * reads/writes keep the optimistic cache but are never retried automatically. */
  _save(storageKey, migrate, field, apply) {
    const result = apply(this[field]).result;
    const pending = { field, apply };
    const generation = this._cacheGeneration;
    this._pendingChanges.push(pending);
    const forget = () => { this._pendingChanges = this._pendingChanges.filter(change => change !== pending); };
    this._coordinate(() => {
      const read = readJSONStrict(storageKey);
      if (!read.ok) throw Object.assign(new Error(read.code, { cause: read.cause }), { code: read.code });
      const doc = migrate(read.value);
      const { changed } = apply(doc);
      if (changed) writeJSONOrThrow(storageKey, doc);
      forget();
      if (generation === this._cacheGeneration && !(field === '_a' && this._serverMode)) this._refresh(field, doc);
    }).catch(forget);
    return result;
  }

  /** The live context for a read, or null (the last captured key then). */
  _context(systemId, romHash) {
    const live = this._captureContext(systemId, romHash);
    return live.ok ? live.ctx : null;
  }

  seedUnlocks(systemId, romHash, achievementList) {
    const key = gameKey(systemId, romHash);
    const entry = {};
    for (const ach of achievementList) {
      if (ach.unlocked && ach.unlocked_at) {
        entry[ach.slug] = new Date(ach.unlocked_at).getTime();
      }
    }
    this._a.unlocked[key] = entry;
  }

  // ── Achievements ─────────────────────────────────────────

  /** Returns an object of achievement id → unlock timestamp (ms). */
  getUnlocked(systemId, romHash) {
    return this._a.unlocked[gameKey(systemId, romHash)] || {};
  }

  /**
   * Record an unlock. Returns true if this was a new unlock, false if it
   * was already unlocked (so callers can skip the toast). The answer is this
   * tab's, as before: an unlock another tab stored first keeps its stored
   * time and core, and this tab still counts and toasts its own.
   */
  unlock(systemId, romHash, achievementId, timestamp = Date.now()) {
    const key = gameKey(systemId, romHash);
    if (this._a.unlocked[key]?.[achievementId]) return false;
    // The unlock's core, for a later scope check (spec 6.4).
    const coreName = this._context(systemId, romHash)?.coreName || null;
    const record = (doc) => {
      const entry = doc.unlocked[key] || (doc.unlocked[key] = {});
      if (entry[achievementId]) return { result: true, changed: false };
      entry[achievementId] = timestamp;
      if (coreName) {
        const cores = doc.cores || (doc.cores = {});
        (cores[key] || (cores[key] = {}))[achievementId] = coreName;
      }
      return { result: true, changed: true };
    };
    // Server mode stores no unlock: memory only, as before.
    return this._serverMode ? record(this._a).result : this._save(ACHIEVEMENTS_KEY, migrateAchievements, '_a', record);
  }

  /** The core recorded with an unlock, or null (older records have none). */
  getUnlockCore(systemId, romHash, achievementId) {
    const cores = this._a.cores?.[gameKey(systemId, romHash)];
    const core = cores && Object.prototype.hasOwnProperty.call(cores, achievementId) ? cores[achievementId] : null;
    return typeof core === 'string' && core.length > 0 ? core : null;
  }

  /**
   * Asynchronously remove one refused unlock (spec 7.7), in either mode, against the latest persisted state read strictly
   * (removeEverywhere): true once its absence is stored, false when absent from memory and storage alike, the
   * structured failure `{ ok: false, code, cause }` (store_read_failed, store_unreadable) when the persisted
   * state cannot be read or parsed (the unlock stays, memory and storage alike, for the retry); a failed write
   * restores it and throws. Server mode stores only local unlocks: the persisted document is written, never
   * memory's server unlocks.
   */
  removeUnlock(systemId, romHash, achievementId, guard) {
    const key = gameKey(systemId, romHash);
    // Own properties only ('toString' never matches).
    return this._coordinate(() => removeEverywhere(ACHIEVEMENTS_KEY, migrateAchievements, this._a,
      (doc) => isRecord(doc.unlocked?.[key]) && own(doc.unlocked[key], achievementId),
      (doc) => [[doc.unlocked?.[key], achievementId], [doc.cores?.[key], achievementId]]), guard)
      .catch(error => { if (error.code === 'store_lock_unavailable') return { ok: false, code: error.code, cause: error }; throw error; });
  }

  /**
   * Asynchronously clear one game's unlocks and stats: true once cleared, else the structured failure `{ ok: false, code, cause }`
   * (store_read_failed, store_unreadable) when a persisted document cannot be read or parsed, and nothing is cleared.
   * The clear is acknowledged only after its transaction completes and is written to the latest persisted documents, read strictly as in
   * removeEverywhere, so another tab's records for other games stay. Server mode stores no unlocks: its unlocks
   * clear in memory only, as before.
   */
  resetGame(systemId, romHash) {
    return this._coordinate(() => {
      const key = gameKey(systemId, romHash);
      const plans = [[STATS_KEY, migrateStats, (doc) => [[doc.games, key]]]];
      if (!this._serverMode) plans.push([ACHIEVEMENTS_KEY, migrateAchievements, (doc) => [[doc.unlocked, key], [doc.cores, key]]]);
      const persisted = [];
      for (const [storageKey, migrate, entries] of plans) {
        const read = readJSONStrict(storageKey);
        if (!read.ok) return readRefusal(read);
        persisted.push([storageKey, migrate(read.value), entries]);
      }
      for (const [storageKey, doc, entries] of persisted) {
        let changed = false;
        for (const [map, id] of entries(doc)) {
          if (isRecord(map) && own(map, id)) { delete map[id]; changed = true; }
        }
        if (changed) writeJSONOrThrow(storageKey, doc);
      }
      for (const [map, id] of [[this._a.unlocked, key], [this._a.cores, key], [this._s.games, key]]) {
        if (isRecord(map)) delete map[id];
      }
      delete this._sessionStartAt[key];
      this._refresh('_s', persisted[0][1]);
      if (!this._serverMode) this._refresh('_a', persisted[1][1]);
      return true;
    }).catch(error => ({ ok: false, code: error.code, cause: error }));
  }

  /** Wipe everything. Intentional, so the empty documents replace whatever is stored, never merged with it. */
  resetAll() {
    this._cacheGeneration++;
    this._a = emptyAchievements();
    this._s = emptyStats();
    this._sessionStartAt = {};
    const serverMode = this._serverMode;
    return this._coordinate(() => {
      if (!serverMode) writeJSONOrThrow(ACHIEVEMENTS_KEY, emptyAchievements());
      writeJSONOrThrow(STATS_KEY, emptyStats());
      return true;
    }).catch(error => ({ ok: false, code: error.code, cause: error }));
  }

  // ── Stats ────────────────────────────────────────────────

  /** Returns the full stats record (zeros / nulls if none). */
  getStats(systemId, romHash) {
    const existing = this._s.games[gameKey(systemId, romHash)];
    if (!existing) return emptyStatsRecord();
    // Defaults fill missing fields.
    const stats = { ...emptyStatsRecord(), ...existing };
    delete stats.authoredBests;
    delete stats.activeScoreKey;
    delete stats.highScoreCore;
    // The live definition's best (spec 7.9).
    const best = this._bestFor(systemId, romHash, existing);
    stats.highScore = best ? best.highScore : null;
    stats.highScoreAt = best ? best.highScoreAt : 0;
    return stats;
  }

  /** The live context's best key, or null. */
  _bestKey(systemId, romHash, g) {
    const ctx = this._context(systemId, romHash);
    if (ctx) return ctx.authored ? authoredBestKey(ctx.scoreVersionId, ctx.scoreEpoch) : STATIC_SCORE_KEY;
    return g.activeScoreKey || STATIC_SCORE_KEY;
  }

  /** That key's cached best, or null (an unusable authored one included). */
  _bestFor(systemId, romHash, g) {
    const key = this._bestKey(systemId, romHash, g);
    if (key == null) return null;
    if (key !== STATIC_SCORE_KEY) return authoredBestAt(g, key);
    if (g.highScore == null) return null;
    return { highScore: g.highScore, highScoreAt: g.highScoreAt || 0, coreName: g.highScoreCore || null, scoreVersionId: null, scoreEpoch: null };
  }

  /**
   * Start a new session: increments `sessions`, sets `lastPlayed`, and
   * on the very first session sets `firstPlayed`. Also stashes the
   * start timestamp so `endSession()` can compute duration. The session
   * is counted onto the stored count (`_save`); the dates keep the newest
   * last-played and the earliest first-played time.
   */
  startSession(systemId, romHash, now = Date.now()) {
    const key = gameKey(systemId, romHash);
    this._save(STATS_KEY, migrateStats, '_s', (doc) => {
      mergeStats(doc.games[key] || (doc.games[key] = emptyStatsRecord()), { sessions: 1, lastPlayed: now, firstPlayed: now });
      return { result: undefined, changed: true };
    });
    this._sessionStartAt[key] = now;
  }

  /**
   * End the current session: updates `longestSessionMs` if this
   * session is the longest so far. Idempotent — calling without a
   * matching `startSession` is a no-op. A longer session another tab
   * stored stays, and a game another tab cleared stays cleared.
   */
  endSession(systemId, romHash, now = Date.now()) {
    const key = gameKey(systemId, romHash);
    const started = this._sessionStartAt[key];
    if (!started) return;
    delete this._sessionStartAt[key];
    const sessionMs = now - started;
    this._save(STATS_KEY, migrateStats, '_s', (doc) => {
      const g = doc.games[key];
      if (!g || !(sessionMs > (g.longestSessionMs || 0))) return { result: undefined, changed: false };
      g.longestSessionMs = sessionMs;
      return { result: undefined, changed: true };
    });
  }

  /** Add elapsed ms to the current game's play time: this delta onto the stored total, never a total from this tab's cache. */
  addPlayTime(systemId, romHash, deltaMs) {
    if (!deltaMs || deltaMs <= 0) return;
    const key = gameKey(systemId, romHash);
    this._save(STATS_KEY, migrateStats, '_s', (doc) => {
      mergeStats(doc.games[key] || (doc.games[key] = emptyStatsRecord()), { playMs: deltaMs });
      return { result: undefined, changed: true };
    });
  }

  /**
   * Record a high-score observation. Returns whether this tab captured a new
   * best, synchronously; another tab may already have a higher stored best.
   * Only persists when `score` is a
   * finite number strictly greater than the stored value. Safe to
   * call every tick — cheap no-op for non-improving scores: storage is
   * read only when this tab's cache sees a new best or a new active key,
   * and the capture is then recorded against the stored record (`_save`),
   * where a higher best another tab stored wins.
   */
  updateHighScore(systemId, romHash, score, now = Date.now()) {
    if (typeof score !== 'number' || !Number.isFinite(score) || score <= 0) return false;
    const key = gameKey(systemId, romHash);
    // Provenance first: without it no best changes (spec 7.9).
    const capture = this._captureContext(systemId, romHash);
    if (!capture.ok) return false;
    const ctx = capture.ctx;
    // Per version and epoch for an authored definition (spec 7.9).
    const bestKey = ctx?.authored ? authoredBestKey(ctx.scoreVersionId, ctx.scoreEpoch) : STATIC_SCORE_KEY;
    if (!bestKey) return false;
    const seen = scoreChange(this._s.games[key] || emptyStatsRecord(), bestKey, score);
    if (!seen.key && !seen.best) return false;
    return this._save(STATS_KEY, migrateStats, '_s', (doc) => {
      const g = doc.games[key] || emptyStatsRecord();
      const recorded = recordScore(g, bestKey, score, now, ctx);
      if (recorded.changed) doc.games[key] = g;
      return recorded;
    });
  }

  /** The best to send at session end. */
  getScoreForSend(systemId, romHash) {
    const none = { highScore: null, highScoreAt: 0, coreName: null, scoreVersionId: null, scoreEpoch: null };
    const g = this._s.games[gameKey(systemId, romHash)];
    if (!g) return none;
    const best = this._bestFor(systemId, romHash, g);
    return best ? { ...best } : none;
  }

  /** Every usable cached best for one game, static first (an authored one only with its key's version and epoch). */
  listScoreEntries(systemId, romHash) {
    const g = this._s.games[gameKey(systemId, romHash)];
    if (!g) return [];
    const out = [];
    if (g.highScore != null) {
      out.push({ highScore: g.highScore, highScoreAt: g.highScoreAt || 0, coreName: g.highScoreCore || null, scoreVersionId: null, scoreEpoch: null });
    }
    for (const key of Object.keys(g.authoredBests || {})) {
      const best = authoredBestAt(g, key);
      if (best) out.push(best);
    }
    return out;
  }

  /**
   * Asynchronously drop a refused authored best (spec 7.9), only its version and epoch, against the latest persisted stats read
   * strictly (removeEverywhere): true once its absence is stored, false when absent from memory and storage alike,
   * the structured failure `{ ok: false, code, cause }` (store_read_failed, store_unreadable) when the persisted
   * stats cannot be read or parsed (the best stays for the retry); a failed write restores it and throws.
   */
  discardAuthoredBest(systemId, romHash, scoreVersionId, scoreEpoch, guard) {
    const bestKey = authoredBestKey(scoreVersionId, scoreEpoch);
    if (!bestKey) return false;
    const key = gameKey(systemId, romHash);
    const bests = (doc) => (isRecord(doc.games) && own(doc.games, key) && isRecord(doc.games[key]) ? doc.games[key].authoredBests : null);
    // The discarded key stays active: no best is shown, never the static one.
    return this._coordinate(() => removeEverywhere(STATS_KEY, migrateStats, this._s,
      (doc) => isRecord(bests(doc)) && own(bests(doc), bestKey),
      (doc) => [[bests(doc), bestKey]]), guard)
      .catch(error => { if (error.code === 'store_lock_unavailable') return { ok: false, code: error.code, cause: error }; throw error; });
  }

  // ── Inspection helpers for UI ────────────────────────────

  /** List all game keys that have ever been played or have unlocks. */
  listTrackedGames() {
    const keys = new Set();
    for (const k of Object.keys(this._a.unlocked)) keys.add(k);
    for (const k of Object.keys(this._s.games)) keys.add(k);
    return Array.from(keys).map(k => {
      const [systemId, romHash] = k.split(':');
      return { systemId, romHash, key: k };
    });
  }

  /**
   * Aggregate totals across every tracked game. Cheap — iterates all
   * games once. Future SQL equivalent: SUM over play_stats.
   */
  getTotals() {
    let playMs = 0;
    let sessions = 0;
    let firstPlayed = 0;
    let lastPlayed = 0;
    const games = Object.values(this._s.games);
    for (const g of games) {
      playMs += g.playMs || 0;
      sessions += g.sessions || 0;
      if (g.firstPlayed && (!firstPlayed || g.firstPlayed < firstPlayed)) {
        firstPlayed = g.firstPlayed;
      }
      if (g.lastPlayed > lastPlayed) lastPlayed = g.lastPlayed;
    }
    return {
      playMs,
      sessions,
      uniqueGames: games.length,
      firstPlayed,
      lastPlayed,
    };
  }

  /**
   * Return a Map<systemId, totalPlayMs>. Used by the stats page to
   * render "favorite system" bars.
   */
  getPlayMsBySystem() {
    const out = new Map();
    for (const [key, g] of Object.entries(this._s.games)) {
      const [systemId] = key.split(':');
      out.set(systemId, (out.get(systemId) || 0) + (g.playMs || 0));
    }
    return out;
  }

  /** Top N games sorted by total play time (descending). */
  getTopGamesByPlayMs(limit = 10) {
    return this._listTrackedWithStats()
      .filter(e => (e.stats.playMs || 0) > 0)
      .sort((a, b) => (b.stats.playMs || 0) - (a.stats.playMs || 0))
      .slice(0, limit);
  }

  /** Top N games sorted by session count (descending). */
  getTopGamesBySessions(limit = 10) {
    return this._listTrackedWithStats()
      .filter(e => (e.stats.sessions || 0) > 0)
      .sort((a, b) => (b.stats.sessions || 0) - (a.stats.sessions || 0))
      .slice(0, limit);
  }

  /** Most-recently-played games (descending by lastPlayed). */
  getRecentlyPlayed(limit = 10) {
    return this._listTrackedWithStats()
      .filter(e => (e.stats.lastPlayed || 0) > 0)
      .sort((a, b) => (b.stats.lastPlayed || 0) - (a.stats.lastPlayed || 0))
      .slice(0, limit);
  }

  /** Internal: list every tracked game with its stats record attached. */
  _listTrackedWithStats() {
    return this.listTrackedGames().map(g => ({
      systemId: g.systemId,
      romHash: g.romHash,
      key: g.key,
      stats: this.getStats(g.systemId, g.romHash),
    }));
  }
}
